import { config } from "./config.js";
import { currentSession } from "./auth/session.js";
import type { WorkspaceView } from "@domain/workspace-view.js";
import type { BuildCycle } from "@domain/entities.js";
import type { CycleStep } from "@domain/steps.js";

/**
 * Calling the API as the signed-in user.
 *
 * The token is fetched per request rather than held from sign-in, because
 * `currentSession` refreshes an id token that has aged out. An hour-old tab
 * that cached its token at sign-in starts 401-ing; one that asks each time
 * does not.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const request = async <T>(
  path: string,
  method: "GET" | "POST" | "PUT" = "GET",
  body?: unknown,
): Promise<T> => {
  const session = await currentSession();
  if (!session) {
    throw new ApiError(401, "Not signed in.");
  }

  const response = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: {
      authorization: `Bearer ${session.idToken}`,
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (!response.ok) {
    /*
     * A 403 here is not a bug to smooth over — it is the tenant boundary doing
     * its job, and the message says so, because the alternative is a developer
     * spending an afternoon on what looks like a routing fault.
     */
    if (response.status === 403) {
      throw new ApiError(
        403,
        "Refused: your session does not carry that workspace. This is " +
          "`withTenantPath` comparing the path against your token.",
      );
    }
    const payload = (await response.json().catch(() => undefined)) as
      | { error?: { message?: unknown } }
      | undefined;
    const detail = payload?.error?.message;
    throw new ApiError(
      response.status,
      typeof detail === "string" && detail.trim() !== ""
        ? detail
        : `${path} returned HTTP ${response.status}.`,
    );
  }

  return (await response.json()) as T;
};

/**
 * What the API says about the caller's own workspace request.
 *
 * Mirrors `RequestStatusView` in `api/src/lib/workspace/approval.ts`. Declared
 * here rather than imported because the API's `lib/workspace` is server code —
 * it pulls in the AWS SDK — and only `lib/domain` is aliased for the browser.
 * The shape is small and the route is versionless; if that stops being true,
 * move the type into `domain` and import it, the way `steps.ts` is imported.
 */
export interface WorkspaceRequestStatus {
  readonly state: "pending" | "approved" | "declined" | "none";
  readonly reason?: string;
  readonly requestedAt?: string;
}

/**
 * `ACC-004-AC1` and `ACC-004-AC2`. One of exactly two routes a pending session
 * may reach, and the only place a declined user learns they were declined —
 * the token cannot say, because the trigger that mints it does no I/O.
 */
export const fetchWorkspaceRequest = async (): Promise<WorkspaceRequestStatus> =>
  request<WorkspaceRequestStatus>("/me/workspace-request");

/**
 * The live tenant data behind the rail.
 *
 * A 404 is possible only for users approved before workspace provisioning was
 * added. PUT is the explicit, idempotent compatibility operation; ordinary new
 * sessions take the read-only GET path.
 */
export const fetchWorkspace = async (
  tenantId: string,
): Promise<WorkspaceView> => {
  const path = `/t/${encodeURIComponent(tenantId)}/workspace`;
  try {
    return await request<WorkspaceView>(path);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return request<WorkspaceView>(path, "PUT");
    }
    throw error;
  }
};

export const saveDescription = async (
  tenantId: string,
  description: string,
): Promise<BuildCycle> =>
  request<BuildCycle>(
    `/t/${encodeURIComponent(tenantId)}/workspace/description`,
    "PUT",
    { description },
  );

/** Continue is deliberately separate from saving and performs no generation. */
export const continueFromDescribe = async (
  tenantId: string,
): Promise<BuildCycle> =>
  request<BuildCycle>(
    `/t/${encodeURIComponent(tenantId)}/workspace/description/continue`,
    "POST",
  );

export const rememberCurrentStep = async (
  tenantId: string,
  step: CycleStep,
): Promise<BuildCycle> =>
  request<BuildCycle>(
    `/t/${encodeURIComponent(tenantId)}/workspace/current-step`,
    "PUT",
    { step },
  );

export interface ClarificationGenerationResponse {
  readonly view: WorkspaceView;
}

const clarificationPath = (tenantId: string): string =>
  `/t/${encodeURIComponent(tenantId)}/workspace/clarifications`;

export const generateClarifications = async (
  tenantId: string,
  requestId: string,
): Promise<ClarificationGenerationResponse> =>
  request<ClarificationGenerationResponse>(
    `${clarificationPath(tenantId)}/generate`,
    "POST",
    { requestId },
  );

export const cancelClarificationGeneration = async (
  tenantId: string,
  requestId: string,
): Promise<BuildCycle> =>
  request<BuildCycle>(`${clarificationPath(tenantId)}/cancel`, "POST", {
    requestId,
  });

export const answerClarification = async (
  tenantId: string,
  questionId: string,
  answer: string,
  requestId: string,
): Promise<WorkspaceView> =>
  request<WorkspaceView>(
    `${clarificationPath(tenantId)}/${encodeURIComponent(questionId)}/answer`,
    "PUT",
    { answer, requestId },
  );

export const skipClarification = async (
  tenantId: string,
  questionId: string,
  requestId: string,
): Promise<WorkspaceView> =>
  request<WorkspaceView>(
    `${clarificationPath(tenantId)}/${encodeURIComponent(questionId)}/skip`,
    "PUT",
    { requestId },
  );

export const continueFromClarify = async (
  tenantId: string,
): Promise<BuildCycle> =>
  request<BuildCycle>(`${clarificationPath(tenantId)}/continue`, "POST");

export interface PendingWorkspaceRequest {
  readonly userId: string;
  readonly email: string;
  readonly state: "pending";
  readonly createdAt: string;
}

export interface ApprovedWorkspaceRequest {
  readonly userId: string;
  readonly email: string;
  readonly state: "approved";
  readonly tenantId: string;
  readonly decidedAt: string;
}

export interface TenantCatalogEntry {
  readonly tenantId: string;
  readonly primaryAccountUserId: string;
  readonly primaryAccountEmail: string;
  readonly createdAt: string;
}

export interface TenantCatalogPage {
  readonly tenants: readonly TenantCatalogEntry[];
  readonly nextCursor?: string;
}

/** The durable `_platform` tenant directory (`ACC-005-AC6`). */
export const fetchTenantCatalog = async (
  cursor?: string,
): Promise<TenantCatalogPage> =>
  request<TenantCatalogPage>(
    `/t/_platform/tenants${
      cursor === undefined ? "" : `?cursor=${encodeURIComponent(cursor)}`
    }`,
  );

/** The `_platform` administrator's review queue (`ACC-005-AC1`). */
export const fetchPendingWorkspaceRequests = async (): Promise<
  readonly PendingWorkspaceRequest[]
> => {
  const result = await request<{ requests: PendingWorkspaceRequest[] }>(
    "/t/_platform/workspace-requests",
  );
  return result.requests;
};

export const approveWorkspaceRequest = async (
  userId: string,
): Promise<ApprovedWorkspaceRequest> =>
  request<ApprovedWorkspaceRequest>(
    `/t/_platform/workspace-requests/${encodeURIComponent(userId)}/approve`,
    "POST",
  );

export const declineWorkspaceRequest = async (
  userId: string,
  reason: string,
): Promise<void> => {
  await request(
    `/t/_platform/workspace-requests/${encodeURIComponent(userId)}/decline`,
    "POST",
    { reason },
  );
};
